import React, { useState } from 'react';
import { Server, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Circle as XCircle, Eye, EyeOff, ExternalLink, Loader as Loader2, Unplug, Plug } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useXcloudConnection, useConnectXCloud, useDisconnectXCloud } from '@/hooks/useXcloud';

interface Workspace {
  id: string;
  name: string;
  color?: string;
  logo?: string;
  thumbnail?: string;
}

interface IntegrationsTabProps {
  workspace: Workspace;
  isGlobalDarkMode: boolean;
}

function XCloudIntegrationCard({
  workspaceId,
  isAdmin,
}: {
  workspaceId: string;
  isAdmin: boolean;
}) {
  const { data: integration, isLoading } = useXcloudConnection(workspaceId);
  const connectMutation = useConnectXCloud(workspaceId);
  const disconnectMutation = useDisconnectXCloud(workspaceId);

  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);

  const isConnected = integration?.status === 'connected';
  const isInvalid = integration?.status === 'invalid';

  const handleConnect = async () => {
    if (!token.trim()) {
      toast.error('Please enter your xCloud API token');
      return;
    }
    try {
      const result = await connectMutation.mutateAsync({ token: token.trim() });
      toast.success(`xCloud connected${result.email ? ` as ${result.email}` : ''}`);
      setShowConnectDialog(false);
      setToken('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to connect xCloud');
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync();
      toast.success('xCloud disconnected');
      setShowDisconnectDialog(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect xCloud');
    }
  };

  const statusBadge = () => {
    if (isLoading) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
        <Loader2 className="w-3 h-3 animate-spin" /> Checking...
      </span>
    );
    if (isConnected) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 className="w-3 h-3" /> Connected
      </span>
    );
    if (isInvalid) return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-600 dark:text-red-400">
        <AlertCircle className="w-3 h-3" /> Token Invalid
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
        <XCircle className="w-3 h-3" /> Not Connected
      </span>
    );
  };

  return (
    <>
      <div className="rounded-xl border border-border bg-card p-5 flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-sky-500/10 flex items-center justify-center">
          <Server className="w-5 h-5 text-sky-500" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-semibold text-foreground text-sm">xCloud Hosting</h3>
            {statusBadge()}
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            Connect your xCloud account to manage servers and sites directly from the CRM Hosting tab.
          </p>

          {(isConnected || isInvalid) && integration?.metadata?.email && (
            <p className="text-xs text-muted-foreground mb-3">
              Connected as <span className="font-medium text-foreground">{integration.metadata.email}</span>
              {integration.metadata.validated_at && (
                <> · {new Date(integration.metadata.validated_at).toLocaleDateString()}</>
              )}
            </p>
          )}

          {isInvalid && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/8 border border-red-500/20 mb-3">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-600 dark:text-red-400">
                Your API token has expired or been revoked. Please reconnect with a new token.
              </p>
            </div>
          )}

          {/* Actions */}
          {isAdmin ? (
            <div className="flex items-center gap-2 flex-wrap">
              {!isConnected ? (
                <button
                  onClick={() => setShowConnectDialog(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-medium transition-colors"
                >
                  <Plug className="w-3.5 h-3.5" />
                  {isInvalid ? 'Reconnect' : 'Connect'}
                </button>
              ) : (
                <button
                  onClick={() => setShowDisconnectDialog(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border hover:bg-muted text-foreground text-xs font-medium transition-colors"
                >
                  <Unplug className="w-3.5 h-3.5" />
                  Disconnect
                </button>
              )}
              <a
                href="https://app.xcloud.host/user/api-tokens"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Get API Token <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">Only workspace owners and admins can manage integrations.</p>
          )}
        </div>
      </div>

      {/* Connect Dialog */}
      {showConnectDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowConnectDialog(false)} />
          <div className="relative w-full max-w-md bg-card rounded-2xl border border-border shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-sky-500/10 flex items-center justify-center">
                <Server className="w-5 h-5 text-sky-500" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Connect xCloud</h2>
                <p className="text-xs text-muted-foreground">Enter your Personal Access Token</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">API Token</label>
                <div className="relative">
                  <input
                    type={showToken ? 'text' : 'password'}
                    value={token}
                    onChange={e => setToken(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleConnect()}
                    placeholder="Paste your xCloud API token..."
                    className="w-full px-3 py-2.5 pr-10 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Generate a token in your{' '}
                  <a
                    href="https://app.xcloud.host/user/api-tokens"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-500 hover:underline inline-flex items-center gap-0.5"
                  >
                    xCloud API settings <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => { setShowConnectDialog(false); setToken(''); }}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnect}
                  disabled={connectMutation.isPending || !token.trim()}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors inline-flex items-center justify-center gap-2"
                >
                  {connectMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Connecting...</>
                  ) : (
                    <><Plug className="w-4 h-4" /> Connect</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Confirm Dialog */}
      {showDisconnectDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDisconnectDialog(false)} />
          <div className="relative w-full max-w-sm bg-card rounded-2xl border border-border shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Unplug className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Disconnect xCloud?</h2>
                <p className="text-xs text-muted-foreground">This will remove your API token</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              The xCloud API token will be permanently deleted and all cached hosting data for this workspace will be cleared. You can reconnect at any time.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDisconnectDialog(false)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDisconnect}
                disabled={disconnectMutation.isPending}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-medium transition-colors inline-flex items-center justify-center gap-2"
              >
                {disconnectMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Disconnecting...</>
                ) : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export const IntegrationsTab: React.FC<IntegrationsTabProps> = ({ workspace, isGlobalDarkMode: _ }) => {
  const { userRole } = useWorkspace();
  const isAdmin = userRole === 'owner' || userRole === 'admin';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-1 text-foreground">Integrations</h2>
        <p className="text-sm text-muted-foreground">
          Manage workspace integrations and connected services
        </p>
      </div>

      <div className="space-y-3">
        <XCloudIntegrationCard workspaceId={workspace.id} isAdmin={isAdmin} />
      </div>
    </div>
  );
};
